import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, PlayCircle, Zap, Trophy, Clock, Flame, Target, Medal, ArrowRight, FileCheck2, Flag } from "lucide-react";
import { ProgressRing, Stat } from "@/components/academy/AcademyParts";
import { CertificateCard } from "@/components/academy/CertificateCard";
import { fmtDuration, DISCLAIMER } from "@/lib/academy";

function PathNode({ m, onOpen }) {
  const Icon = m.passed ? CheckCircle2 : m.unlocked ? PlayCircle : Lock;
  const tone = m.passed ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : m.unlocked ? "text-blue-400 border-blue-500/40 bg-blue-500/10" : "text-zinc-600 border-zinc-800 bg-zinc-900";
  const num = m.kind === "capstone" ? "C" : m.kind === "final" ? "F" : m.kind === "simulation" ? "S" : m.order;
  const detail = m.kind === "capstone" ? `${m.cases_done}/${m.cases_total} cases · ${m.points} pts` : m.kind === "final" ? (m.best_pct != null ? `Best ${m.best_pct}%` : "20 questions") : m.kind === "simulation" ? (m.best_pct != null ? `Best ${m.best_pct}% · pass 90%` : "7 incoming reports · pass 90%") : `${m.lessons_done}/${m.lessons_total} lessons${m.best_pct != null ? ` · best ${m.best_pct}%` : ""}`;
  return (
    <button data-testid={`academy-path-${m.id}`} disabled={!m.unlocked} onClick={() => onOpen(m.id)}
      className="group relative flex w-full items-center gap-4 rounded-xl border border-zinc-800/70 bg-[#111113] p-4 text-left transition-[border-color,transform] hover:-translate-y-0.5 hover:border-blue-500/50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:border-zinc-800/70">
      <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl border text-lg font-bold ${tone}`}>{m.unlocked && !m.passed ? num : <Icon size={20} />}</div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-zinc-50">{m.title}</span>
          <span className="rounded-full border border-zinc-800 px-2 py-0.5 text-[10px] uppercase tracking-wider text-zinc-500">{m.minutes} min</span>
        </div>
        <div className="mt-0.5 truncate text-[13px] text-zinc-500">{m.subtitle}</div>
        <div className="mt-2 flex items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-800"><div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-[#FFCC00]" style={{ width: `${m.pct}%` }} /></div>
          <span className="w-9 text-right text-[12px] text-zinc-400">{m.pct}%</span>
        </div>
        <div className="mt-1 text-[12px] text-zinc-500">{detail}</div>
      </div>
      <div className="hidden text-right sm:block">
        <div className={`text-[12px] font-semibold ${m.passed ? "text-[#FFCC00]" : "text-zinc-600"}`}><Medal size={14} className="mr-1 inline" />{m.badge}</div>
      </div>
    </button>
  );
}

export function AcademyDashboard({ data, reload }) {
  const navigate = useNavigate();
  const open = (id) => navigate(`/academy/m/${id}`);
  const cont = data.continue;
  const contMod = data.modules.find((m) => m.id === cont?.module_id);
  return (
    <div className="space-y-8" data-testid="academy-dashboard">
      <section className="grid gap-6 rounded-2xl border border-zinc-800/70 bg-gradient-to-br from-[#0b1430] to-[#0c0c0e] p-6 md:grid-cols-[auto_1fr] md:p-8">
        <ProgressRing pct={data.overall_pct} size={140} label="complete" />
        <div className="flex flex-col justify-center">
          <div className="text-[12px] uppercase tracking-[0.2em] text-blue-400">{data.level}</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl" data-testid="academy-welcome">Welcome back{data.user.name ? `, ${data.user.name}` : ""}</h1>
          <p className="mt-2 max-w-xl text-[14px] text-zinc-400">Report It Right: 8 modules, 5 case files, a final exam and a live Ops Desk simulation. Short sessions, at your own pace.</p>
          {contMod && (
            <button data-testid="academy-continue" onClick={() => open(contMod.id)}
              className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-blue-500">
              Continue: {contMod.title} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Stat icon={Zap} label="XP" value={data.xp} testid="academy-stat-xp" />
        <Stat icon={Target} label="Avg score" value={data.avg_score != null ? `${data.avg_score}%` : "–"} testid="academy-stat-avg" />
        <Stat icon={Trophy} label="Badges" value={`${data.badges.length}/${data.modules.length}`} testid="academy-stat-badges" />
        <Stat icon={Clock} label="Learning time" value={fmtDuration(data.seconds)} testid="academy-stat-time" />
        <Stat icon={Flame} label="Day streak" value={data.streak} testid="academy-stat-streak" />
        <Stat icon={FileCheck2} label="Certificate" value={data.certificate ? "Earned" : data.certificate_ready ? "Ready" : "Locked"} testid="academy-stat-cert" />
      </section>

      <section>
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-zinc-50"><Flag size={18} className="text-blue-400" /> Your learning path</h2>
        <div className="grid gap-3 lg:grid-cols-2">{data.modules.map((m) => <PathNode key={m.id} m={m} onOpen={open} />)}</div>
      </section>

      <CertificateCard data={data} reload={reload} />
      <p className="text-[12px] leading-relaxed text-zinc-600" data-testid="academy-disclaimer">{DISCLAIMER}</p>
    </div>
  );
}
